<!-- EventCalendar.vue -->
<script setup lang="ts">
import { computed, ref } from "vue";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  CalendarOptions,
  ToolbarInput,
  EventClickArg,
  EventHoveringArg,
} from "@fullcalendar/core";
import { useI18n } from "vue-i18n";

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  color?: string;
}

const props = withDefaults(
  defineProps<{
    events: CalendarEvent[];
    headerToolbar?: ToolbarInput;
    clickable?: boolean;
    callBack?: (id: string) => void;
  }>(),
  {
    events: () => [],
    headerToolbar: () => ({
      left: "",
      center: "",
      right: "",
    }),
    clickable: false,
  },
);

const { locale } = useI18n();

// Map events to FullCalendar's expected shape, applying color per event
const mappedEvents = computed(() =>
  props.events.map((event) => ({
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    backgroundColor: event.color,
    borderColor: event.color,
  })),
);

function handleEventClick(info: EventClickArg) {
  if (!props.clickable) return;
  props.callBack?.(info.event.id);
}

const calendarOptions = computed<CalendarOptions>(() => ({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  headerToolbar: props.headerToolbar,
  locale: locale.value,
  events: mappedEvents.value,
  eventClick: handleEventClick,
  eventMouseEnter: handleEventMouseEnter,
  eventMouseLeave: handleEventMouseLeave,
  eventClassNames: props.clickable ? "cursor-pointer" : "",
  eventTimeFormat: {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // ← forces 24h format, see below
  },
  height: "auto",
}));

// Tooltip state
const tooltip = ref<{
  visible: boolean;
  x: number;
  y: number;
  title: string;
  time: string;
}>({
  visible: false,
  x: 0,
  y: 0,
  title: "",
  time: "",
});

function handleEventMouseEnter(info: EventHoveringArg) {
  tooltip.value = {
    visible: true,
    x: info.jsEvent.clientX,
    y: info.jsEvent.clientY,
    title: info.event.title,
    time:
      info.event.start!.toLocaleTimeString("pt-PT", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }) +
      " -> " +
      info.event.end!.toLocaleTimeString("pt-PT", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
  };
}

function handleEventMouseLeave() {
  tooltip.value.visible = false;
}
</script>

<template>
  <div class="calendar-wrapper relative">
    <FullCalendar :options="calendarOptions" />

    <Teleport to="body">
      <div
        v-if="tooltip.visible"
        class="fixed z-[200] -translate-x-1/2 -translate-y-full pointer-events-none bg-gray-900 text-white text-xs rounded-md px-3 py-2 shadow-lg mb-2"
        :style="{ left: `${tooltip.x}px`, top: `${tooltip.y - 8}px` }"
      >
        <div class="font-semibold">{{ tooltip.title }}</div>
        <div class="text-gray-300">{{ tooltip.time }}</div>
        <div
          class="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-900"
        ></div>
      </div>
    </Teleport>
  </div>
</template>

<style>
/* Move the day number from top-right to top-left */
.calendar-wrapper .fc-daygrid-day-top {
  flex-direction: row;
}

.calendar-wrapper .fc-col-header-cell {
  font-weight: 700px;
  color: var(--color-light-gray);
  font-size: 11px;
}

/* Create visual separation between cells */
.calendar-wrapper .fc-daygrid-day {
  padding: 3px; /* creates the "gap" look since the frame inside has the rounded bg */
}

.calendar-wrapper .fc-daygrid-day-frame {
  border-radius: 8px;
  background-color: var(--color-background);
}

.calendar-wrapper .fc-daygrid-event {
  white-space: pre-wrap;

  .fc-event-time {
    margin-right: 8px;
  }
}

.calendar-wrapper .fc-daygrid-event-harness a {
  border-radius: 6px !important;
}

.calendar-wrapper .fc-event {
  transition: filter 0.15s ease;
  cursor: pointer;
}

.calendar-wrapper .fc-event:hover {
  filter: brightness(0.85);
}

.calendar-wrapper .fc-theme-standard td,
.calendar-wrapper .fc-theme-standard th,
.calendar-wrapper .fc-scrollgrid {
  border: none;
}
</style>
